import { getElementHeight, isElementVisible, getMaxVisibleHeight, getMaxVisibleHeightLegacy } from './domHelpers';

/**
 * Calculate desktop header offset
 * Handles sticky headers, alerts, and shrinking navigation bars
 */
export const getDesktopHeaderOffset = ({ dom, config, headerHeight }) => {
	if (config.isNotSticky) {
		return 0;
	}

	if (
		!config.noShrink &&
		dom.alert?.offsetHeight > 0 &&
		isElementVisible(dom.alert)
	) {
		return Math.max(0, dom.alert.getBoundingClientRect().bottom);
	}

	const desktopNavbar = document.querySelector(
		'.navbar.shrink-header-nav, .navbar.sticky-header-nav'
	);
	const navbarHeight = isElementVisible(desktopNavbar)
		? getElementHeight(desktopNavbar)
		: 0;

	const headerRect = dom.header?.getBoundingClientRect();
	if (headerRect && headerRect.top < 0) {
		return Math.max(0, headerRect.bottom) + navbarHeight;
	}

	return headerHeight + navbarHeight;
};

// Legacy desktop header offset (no translate3d awareness)
export const getDesktopHeaderOffsetLegacy = ({ dom, config, headerHeight }) => {
	if (config.isNotSticky) {
		return 0;
	}

	if (
		!config.noShrink &&
		dom.alert?.offsetHeight > 0 &&
		isElementVisible(dom.alert)
	) {
		return dom.alert.offsetTop + dom.alert.offsetHeight;
	}

	const desktopNavbar = document.querySelector(
		'.navbar.shrink-header-nav, .navbar.sticky-header-nav'
	);
	const navbarHeight = isElementVisible(desktopNavbar)
		? getElementHeight(desktopNavbar)
		: 0;
	return headerHeight + navbarHeight;
};

/**
 * Calculate mobile fixed header offset
 * Handles alert banners and shrunk navigation bars on page load and scroll
 */
export const getMobileFixedHeaderOffset = ({ dom, headerHeight, isPageLoad }) => {
	if (isPageLoad) {
		const alertBanner = dom.header.querySelector('.content-alert-banner');
		if (alertBanner && !alertBanner.classList.contains('hide')) {
			return headerHeight - getElementHeight(alertBanner);
		}
	}

	const shrunkMobileNavbar = document.querySelector(
		'.nav-button-container.shrink-header-nav .navbar.fixed'
	);
	if (shrunkMobileNavbar && isElementVisible(shrunkMobileNavbar)) {
		return getElementHeight(shrunkMobileNavbar);
	}

	const headerRect = dom.header.getBoundingClientRect();
	if (headerRect.top < 0) {
		return Math.max(0, headerRect.bottom);
	}

	return getMaxVisibleHeight(dom.header);
};

/**
 * Calculate mobile default header offset
 * Handles different mobile header types: minimal container, default header with vCard
 */
export const getMobileDefaultHeaderOffset = ({ dom, config }) => {
	// Check for minimal header container first
	const headerMinimalContainer = dom.header.querySelector(
		'.header-minimal-container'
	);
	if (headerMinimalContainer && isElementVisible(headerMinimalContainer)) {
		return getMaxVisibleHeight(headerMinimalContainer);
	}

	// Check for default header
	const headerDefault = dom.header.querySelector('.header-default');
	const headerVCard = dom.header.querySelector('.vcard');
	if (headerDefault && isElementVisible(headerDefault)) {
		// Only include navbar height if it's fixed AND parent doesn't have compact header
		const { mobileNavbar } = config;
		const hasCompactHeader = mobileNavbar?.parentElement?.classList.contains('header-mobile-compact') || false;
		const isNavbarFixed = mobileNavbar?.classList.contains('fixed') || false;
		const shouldIncludeNavbar = isNavbarFixed && !hasCompactHeader;
		const navbarHeight = shouldIncludeNavbar ? getMaxVisibleHeight(mobileNavbar) : 0;
		const vCardHeight = getMaxVisibleHeight(headerVCard);
		return vCardHeight + navbarHeight;
	}

	return getMaxVisibleHeight(config.mobileNavbar) + getMaxVisibleHeight(headerVCard);
};

// Legacy jQuery-based mobile fixed header offset
export const getMobileFixedHeaderOffsetLegacy = ({ dom, headerHeight, isPageLoad }) => {
	if (isPageLoad) {
		const alertBanner = dom.header.querySelector('.content-alert-banner');
		if (alertBanner && !alertBanner.classList.contains('hide')) {
			return headerHeight - getElementHeight(alertBanner);
		}
	}

	const shrunkMobileNavbar = window.$(
		'.nav-button-container.shrink-header-nav .navbar.fixed'
	);
	if (shrunkMobileNavbar.length > 0 && shrunkMobileNavbar.is(':visible')) {
		return getElementHeight(shrunkMobileNavbar[0]);
	}

	return getMaxVisibleHeightLegacy(dom.header);
};

// Legacy jQuery-based mobile default header offset
export const getMobileDefaultHeaderOffsetLegacy = ({ dom, config }) => {
	// Check for minimal header container first
	const headerMinimalContainer = dom.header.querySelector(
		'.header-minimal-container'
	);
	if (headerMinimalContainer && window.$(headerMinimalContainer).is(':visible')) {
		return getMaxVisibleHeightLegacy(headerMinimalContainer);
	}

	// Check for default header
	const headerDefault = dom.header.querySelector('.header-default');
	const headerVCard = dom.header.querySelector('.vcard');
	if (headerDefault && window.$(headerDefault).is(':visible') && isElementVisible(headerDefault)) {
		// Only include navbar height if it's fixed AND parent doesn't have compact header
		const $mobileNavbar = window.$(config.mobileNavbar);
		const hasCompactHeader = $mobileNavbar[0]?.parentElement?.classList.contains('header-mobile-compact') || false;
		const isNavbarFixed = $mobileNavbar.hasClass('fixed');
		const shouldIncludeNavbar = isNavbarFixed && !hasCompactHeader;
		const navbarHeight = shouldIncludeNavbar ? getMaxVisibleHeightLegacy(config.mobileNavbar) : 0;
		const vCardHeight = getMaxVisibleHeightLegacy(headerVCard);
		return vCardHeight + navbarHeight;
	}

	return getMaxVisibleHeightLegacy(config.mobileNavbar) + getMaxVisibleHeightLegacy(headerVCard);
};

// Get header offset based on device and header configuration
export const getHeaderOffset = ({ dom, config, DDC, isPageLoad = false }) => {
	const headerHeight = isElementVisible(dom.header)
		? getElementHeight(dom.header)
		: 0;

	if (!DDC.client.isMobile) {
		const fn = config.isLegacyDom ? getDesktopHeaderOffsetLegacy : getDesktopHeaderOffset;
		return fn({ dom, config, headerHeight });
	}

	if (config.isEntireMobileHeaderFixed) {
		const fn = config.isLegacyDom ? getMobileFixedHeaderOffsetLegacy : getMobileFixedHeaderOffset;
		return fn({ dom, headerHeight, isPageLoad });
	}

	const fn = config.isLegacyDom ? getMobileDefaultHeaderOffsetLegacy : getMobileDefaultHeaderOffset;
	return fn({ dom, config });
};
